import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
// import router from '@/router'
import moment from 'moment'
import store from '@/store'
import { cookies } from '@/util/cookies'
import { iframeAuth } from '@/services/iframeAuth'

export default {
  async executeWithoutLogin (method, resource, data) {
    const options = {
      baseURL: cookies.get('baseUrl') || process.env.VUE_APP_BODYSWAPS_API_ENDPOINT,
      json: true,
      method,
      url: resource,
      data,
      headers: {
        apiKey: cookies.get('apikey') || process.env.VUE_APP_BODYSWAPS_API_KEY,
      },
    }
    return axios(options).then(req => {
      /// console.log(`API call (no login) ${method} ${resource} ${JSON.stringify(data)} returned: ${req.data ? JSON.stringify(req.data) : ''}`)
      return req.data
    }).catch(ex => {
      // console.error(`API call (no login) '${method} ${resource} with body '${JSON.stringify(data)}' failed with response: ${ex}`)
      throw ex
    })
  },

  async executeIframe (method, resource, data, isRetry = false) {
    // If session is already expired with timeout, don't try to refresh
    if (iframeAuth.isSessionExpired()) {
      const error = new Error('Session expired - please refresh the page')
      error.sessionExpired = true
      throw error
    }

    // Get token from iframeAuth service (updated via postMessage)
    const token = iframeAuth.getToken()
    const config = iframeAuth.getConfig()
    const baseUrl = config.baseUrl || cookies.get('baseUrl') || process.env.VUE_APP_BODYSWAPS_API_ENDPOINT
    const accountId = config.accountId || cookies.get('account')
    const userId = config.userId || cookies.get('user')
    const apiKey = config.apiKey || cookies.get('apikey') || process.env.VUE_APP_BODYSWAPS_API_KEY

    if (!token) {
      throw new Error('No authentication token available')
    }

    return axios({
      method,
      baseURL: baseUrl,
      json: true,
      url: resource,
      data,
      headers: {
        Authorization: token,
        'Account-Id': accountId || '',
        userid: userId || '',
        apiKey: apiKey || process.env.VUE_APP_BODYSWAPS_API_KEY,
      },
    }).then(req => {
      return req.data
    }).catch(async (ex) => {
      if (ex.response) {
        console.error(`EXCEPTION: ${ex.response.status} "${ex.response.data}"`)
      }
      // Only retry once, only on 401, and only if session is not expired
      if (ex.response?.status === 401 && !isRetry && !iframeAuth.isSessionExpired()) {
        try {
          await iframeAuth.requestFreshToken()
          return this.executeIframe(method, resource, data, true)
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          // If refresh failed due to session expiry - logout
          if (iframeAuth.isSessionExpired()) {
            const error = new Error('Session expired - please refresh the page')
            error.sessionExpired = true
            throw error
          }
        }
      }
      throw ex
    })
  },

  async execute (method, resource, data) {
    const iframe = process.env.VUE_APP_IS_IFRAME && process.env.VUE_APP_IS_IFRAME === 'true'
    if (iframe) {
      return this.executeIframe(method, resource, data)
    } else {
      const currentUser = firebase.auth().currentUser
      if (currentUser === null) {
        console.log('No user logged in - API call cancelled')
        // router.push('/login')
        return null
      }


      const idToken = await firebase.auth().currentUser.getIdToken(false)
      // console.log(`API REQUEST: ${method.toUpperCase()} ${resource} BODY { ${data ? JSON.stringify(data) : ''} } =>`)
      // let accessToken = await Vue.prototype.$auth.getAccessToken()
      const userID = store.getters.currentUserProfile ? store.getters.currentUserProfile.user.id : ''
      // console.log('Making API call as ' + JSON.stringify(store.getters.currentUserProfile) + ' and authID = ' + firebase.auth().currentUser.uid)
      const apiKey = cookies.get('apikey')
      return axios({
        baseURL: cookies.get('baseUrl') ? cookies.get('baseUrl') : process.env.VUE_APP_BODYSWAPS_API_ENDPOINT,
        json: true,
        method,
        url: resource,
        data,
        headers: {
          sessionToken: idToken, // for logged in users
          userid: userID, // store.getters.currentUserProfile.id,
          apiKey: apiKey ?? process.env.VUE_APP_BODYSWAPS_API_KEY, // this is for anonymous functions
        },
      }).then(req => {
        return req.data
      }).catch(ex => {
        if (ex.response) {
          console.error(`EXCEPTION: ${ex.response.status} "${ex.response.data}"`)
        }
        throw ex
      })
    }
  },

  // Utility to create a query string from a map with an optional allowedKeys array
  mapToQueryString (map, allowedKeys = []) {
    // console.log('map = ' + JSON.stringify(map))
    return Object.keys(map)
      .filter(key => !allowedKeys.length || allowedKeys.includes(key))
      .filter(key => map[key] !== null && map[key] !== undefined)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(map[key])}`)
      .join('&')
  },

  request (type, query) {
    return this.execute(type, query)
  },
  // ---- CLASS CALLS ----- //
  getClasses () {
    return this.execute('get', '/classes')
  },
  getClass (id) {
    return this.execute('get', `/classes/${id}`)
  },
  createClass (data) {
    return this.execute('post', '/classes', data)
  },
  updateClass (id, data) {
    return this.execute('put', `/classes/${id}`, data)
  },
  deleteClass (id) {
    return this.execute('delete', `/classes/${id}`)
  },
  getClassLearnerByInviteCode (code) {
    return this.executeWithoutLogin('get', `/classLearners?inviteCode=${code}`)
  },
  postClassLearner (id, data) {
    return this.execute('post', `/classes/${id}/classLearners`, data)
  },
  getEnrolmentDetails (key, enrolmentEndsAt) {
    return this.executeWithoutLogin('get', `classes/enrolmentDetails?enrolmentKey=${key}&enrolmentEndsAt=${enrolmentEndsAt}`)
  },
  deleteClassLearner (id) {
    return this.execute('delete', `/classLearners/${id}`)
  },
  sendClassLearnerInvite (id, returnURL) {
    return this.execute('put', `/classLearners/${id}/invite`, { returnURL: returnURL })
  },
  getJoinClassShortCode (inviteCode) {
    return this.executeWithoutLogin('get', `/classLearners/shortCode?inviteCode=${inviteCode}`)
  },
  getJoinClassShortCodeNoInvite (id) {
    return this.executeWithoutLogin('get', `/classLearners/${id}/shortCode`)
  },
  getClassShortCode (id, findExisting) {
    return this.execute('get', `/classes/${id}/getPreJoinClassShortCode/${findExisting}`)
  },
  classInviteAll (id, ignoreInvited, returnURL) {
    return this.execute('post', `/classes/${id}/invite?ignoreInvited=${ignoreInvited}`, { returnURL: returnURL })
  },
  // ---- USER CALLS ----- //
  createUser (data) {
    return this.execute('post', '/users', data)
  },
  createUsers (data) {
    return this.execute('post', '/users/bulk', data)
  },
  updateUsers (data) {
    return this.execute('put', '/users/bulk/update', data)
  },
  getUsers (account = undefined, role = undefined, ignoreSelf = false) {
    let query = ''
    if (account) query = query + '&account=' + account
    if (ignoreSelf) query = query + '&ignoreSelf=' + ignoreSelf
    if (role) query = query + '&role=' + role

    return this.execute('get', '/users' + (query !== '' ? ('?' + query.substr(1)) : ''))
  },
  getUsersByAuthID (authID) {
    return this.execute('get', `/users/accounts/${authID}`)
  },
  getUsersByEmail (email) {
    return this.execute('post', '/users/accounts', { email })
  },
  deleteUserHistory (id) {
    return this.execute('delete', `/users/${id}/history`)
  },
  getUser (id) {
    return this.execute('get', `/users/${id}`)
  },
  updateUser (id, data) {
    return this.execute('put', `/users/${id}`, data)
  },
  updateUserPhoneNumberOrPassword (id, data) {
    return this.execute('put', `/users/update-phone-number-password/${id}`, data)
  },
  deleteUser (Ids) {
    return this.execute('delete', '/users', Ids)
  },
  inviteUser (id, returnURL) {
    return this.execute('post', `/users/${id}/invite`, { returnURL: returnURL })
  },
  getUserLoginShortCode (module, lang) {
    if (!module) {
      return this.execute('get', '/users/getLoginShortCode')
    } else {
      return this.execute('get', '/users/getLoginShortCode?module=' + module + `${lang ? '&lang=' + lang : ''}`)
    }
  },
  verifyUserEmail (uuid) {
    return this.execute('put', `/users/verify-user-email/${uuid}`)
  },
  getUserCertificates (id) {
    return this.execute('get', `/users/${id}/certificates`)
  },
  registerUser (code, password) {
    return this.executeWithoutLogin('post', '/users/register', { resetCode: code, password: password })
  },
  revertSecondFactorAddition (email) {
    return this.executeWithoutLogin('post', '/users/revert-second-factor-addition', { email })
  },
  registerThirdPartyUser (source, module, user, isLti, name) {
    // todo - add full name as optional
    return this.executeWithoutLogin('post', `/users/register-third-party-user?source=${source}&module=${module}&user=${user}${isLti ? '&lti=true' : ''}${name ? '&fullName=' + name : ''}`)
  },
  thirdPartyShopIntegration (token, provider) {
    return this.executeWithoutLogin('post', `/users/third-party-shop-integration?token=${token}&provider=${provider}`)
  },
  getUsersStats (account) {
    return this.execute('get', `/users/stats?account=${account}`)
  },
  usersInviteAll (ignoreInvited, returnURL) {
    return this.execute('post', `/users/sendInvites?ignoreInvited=${ignoreInvited}`, { returnURL: returnURL })
  },
  userSelfEnrol (data) {
    return this.executeWithoutLogin('post', '/users/enrol', data)
  },
  generationResetPasswordLink (email) {
    return this.execute('get', `/users/generation-reset-password-link/${email}`)
  },
  // ---- CONTENT CALLS ----- //
  createContent (data) {
    return this.execute('post', '/content', data)
  },
  getContents (showAll = false, locale = 'en', full = true) {
    return this.execute('get', `/content?showAll=${showAll}&locale=${locale}&full=${full}`)
  },
  getContent (id) {
    return this.executeWithoutLogin('get', `/content/${id}`)
  },
  updateContent (id, data) {
    return this.execute('put', `/content/${id}`, data)
  },
  deleteContent (id) {
    return this.execute('delete', `/content/${id}`)
  },
  // ----- CLASS LEARNER CALLS ----- //
  getClassLearner (id) {
    return this.execute('get', `/classLearners/${id}`)
  },
  // ----- ACCOUNTS CALLS ----- //
  resetTotalUsersCount (id) {
    return this.execute('post', `/accounts/${id}/reset-total-users-count`)
  },
  getAccounts (expired = false) {
    return this.execute('get', '/accounts?expired=' + expired)
  },
  getAccount (id) {
    return this.execute('get', `/accounts/${id}`)
  },
  updateAccount (id, data) {
    return this.execute('put', `/accounts/${id}`, data)
  },
  softDeleteAccount (id) {
    return this.execute('get', `/accounts/softDelete/${id}`)
  },
  revertSoftDeleteAccount (id) {
    return this.execute('get', `/accounts/revertSoftDelete/${id}`)
  },
  deleteAccount (id) {
    return this.execute('delete', `/accounts/${id}`)
  },
  createAccount (data) {
    return this.execute('post', '/accounts', data)
  },
  accountRefreshStats (id, data) {
    return this.execute('post', `/accounts/${id}/refreshStats`, data)
  },
  // ------ DEVICES ------ //
  getDevices () {
    return this.execute('get', '/devices')
  },
  updateDevice (id, data) {
    return this.execute('put', `/devices/${id}`, data)
  },
  deleteDevice (id) {
    return this.execute('delete', `/devices/${id}`)
  },
  inviteDeviceShortCode (lifetime) {
    return this.execute('post', '/devices/createShortCode', {})
  },
  getTrialShortCode (data) {
    return this.execute('post', '/devices/createShortCode', data)
  },

  // ------ SHORT CODE ----- //
  generateShortcode (context) {
    return this.executeWithoutLogin('post', '/shortCodes', context)
  },
  generateShortCodeForThirdPartyUser (data) {
    return this.executeWithoutLogin('post', '/users/createShortCodeForThirdPartyUser', data)
  },
  // ------ EVENTS ----- //
  getEventsForLoggedInUser () {
    return this.execute('get', '/events')
  },
  getEventsForClassLearner (classLearnerID) {
    return this.execute('get', `/classLearners/${classLearnerID}/events`)
  },
  getEventsForUser (userID) {
    return this.execute('get', `/users/${userID}/events`)
  },
  getEventCollections (startDate, endDate, selectedScope, key, format, anonymise) {
    return this.execute('get', `/events/get-events-collections-data?format=${format}&key=${key}&startDate=${moment(startDate).format('YYYY-MM-DD')}&endDate=${moment(endDate).format('YYYYMMDD')}&scope=${selectedScope}&anonymise=${anonymise}`)
  },
  // ------ SUBMISSION REPORTS ----- //
  getSubmissionReportsByUser (userID) {
    return this.execute('get', `/submissions/?filters={"userId":{"opp":"eq","val":"${userID}"}}`)
  },
  // ------ REPORTS ------ //
  getUsageReport (startDate, endDate, dimension, account) {
    return this.execute('get', `/reports/usage?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}&dimension=${dimension}&account=${account}`)
  },
  getGlobalUsageReport (startDate, endDate, role) {
    return this.execute('get', `/reports/usage?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}&role=${role}`)
  },
  getClassUsageReport (startDate, endDate, classId, account) {
    return this.execute('get', `/reports/class-usage?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}&classId=${classId}&account=${account}`)
  },
  getSurveyReport (startDate, endDate, options) {
    const query = this.mapToQueryString({
      startdate: moment(startDate).format('YYYY-MM-DD'),
      enddate: moment(endDate).format('YYYY-MM-DD'),
      ...options,
    }, ['startdate', 'enddate', 'account', 'role', 'classid', 'devicetype'])
    return this.execute('get', `/reports/survey?${query}`)
  },
  getClassSurveyReport (startDate, endDate, account, classId) {
    return this.execute('get', `/reports/class-survey?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}&account=${account}&classId=${classId}`)
  },
  getResellersReport (startDate, endDate) {
    return this.execute('get', `/reports/resellers?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}`)
  },
  getAccountsReport (startDate, endDate) {
    return this.execute('get', `/reports/accounts?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}`)
  },
  postAccountsReport (startDate, endDate) {
    return this.execute('post', `/reports/accounts?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}`)
  },
  deleteAccountsReport (startDate, endDate) {
    return this.execute('delete', `/reports/accounts?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}`)
  },
  postResellersReport (startDate, endDate) {
    return this.execute('post', `/reports/resellers?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}`)
  },
  deleteResellersReport (startDate, endDate) {
    return this.execute('delete', `/reports/resellers?startdate=${moment(startDate).format('YYYY-MM-DD')}&enddate=${moment(endDate).format('YYYY-MM-DD')}`)
  },
  getAllAccountsReport () {
    return this.execute('get', '/reports/all-accounts')
  },
  getAllResellersReport () {
    return this.execute('get', '/reports/all-resellers')
  },
  // ------ LOGS ----- //
  getLogs () {
    return this.execute('get', '/logs')
  },
  deleteAllLogs () {
    return this.execute('delete', '/logs')
  },
  deleteLog (id) {
    return this.execute('delete', `/logs/${id}`)
  },
  // ----- CERTIFICATES ------ //
  getCertificate (id) {
    return this.executeWithoutLogin('get', `/certificates/${id}`)
  },
  // ----- DOCUMENTS ----- //
  async uploadDocument (file) {
    return await this.execute('post', '/documents', file)
  },
  async getDocuments () {
    return await this.execute('get', '/documents')
  },
  async getDocument (filename) {
    return await this.execute('get', `/documents/${filename}`)
  },
  async deleteDocuments (id) {
    return await this.execute('delete', `/documents/${id}`)
  },
  // ---- AGREEMENTS ----- //
  async getAgreements () {
    return await this.execute('get', '/agreements')
  },
  // ---- SESSIONS ----- //
  async getSessionsByUser (user) {
    try {
      const response = await this.execute('get', `/sessions?user=${user}`)
      return response
    }
    catch (error) {
      console.log('error', error)
    }
  },

  async getSessionsByClassLearner (classLearner) {
    try {
      const response = await this.execute('get', `/sessions?classLearner=${classLearner}`)
      return response
    }
    catch (error) {
      console.log('error', error)
    }
  },

  getIPELearnerReport (sessionId) {
    return this.execute('get', `/learnerReports/ipe/${sessionId}`)
  },
}
